"use client";

// 4b — trial credits gone early (PANDA-1500). The highest-intent moment: the
// user burned all 100 trial credits before day 3, so they're blocked and
// engaged. We do NOT offer a cheap top-up (that's an escape hatch from the
// recurring plan) — the only path is converting to Pro NOW: end the trial,
// charge Pro today, unlock the full Pro allocation live. Pulls revenue forward
// and locks in at peak intent.
//
// Visual system (PANDA-1500 follow-up): mirrors the canonical billing/upgrade
// modal — the rated credits flow (components/billing/rated) — so it reads as
// part of the same product: shared @base-ui dialog foundation, the same shell,
// the orange-ringed plan card, the gradient "Most popular" pill, emerald checks,
// and the gradient primary CTA at rounded-xl.

import { useEffect, useRef, useState } from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  MODAL_SCROLL_BODY,
  MODAL_VIEWPORT_FRAME_SM,
} from "@/components/ui/modal-surface";
import { Button } from "@/components/ui/button";
import {
  trackTrialExhaustedShown,
  trackTrialConvertClicked,
  trackTrialConvertStarted,
  trackTrialConvertSucceeded,
  trackTrialConvertFailed,
  trackTrialConvertCardUpdate,
} from "@/lib/analytics";
import { redirectToBilling } from "@/lib/intentional-navigation";
import {
  isCustomCheckoutEnabled,
  customCheckoutHref,
} from "@/lib/billing/checkout-mode";
import { isWhop } from "@/lib/payments/provider";
import { resolvePurchaseReturnPath } from "@/lib/billing/purchase-return-path";
import { interceptIfPaymentsPaused } from "@/components/billing/payments-paused-store";
import { resolveTrialExhaustedCopy } from "@/lib/trial/exhausted-copy";

interface ConvertCopy {
  priceLabel: string;
  credits: number;
}

async function fetchRemainingCredits(): Promise<number | null> {
  try {
    const res = await fetch("/api/credits", { cache: "no-store" });
    const d = await res.json();
    return typeof d?.credits_remaining === "number" ? d.credits_remaining : null;
  } catch {
    return null;
  }
}

// After the $29 settles (the convert POST only returns ok once it's PAID —
// payment_behavior:error_if_incomplete), the Pro credits still land via the
// async invoice.payment_succeeded webhook. So we WAIT for them rather than close
// on the stale (still-exhausted) trial balance — the bug the first fix left in
// phase 2. We detect the grant as a real INCREASE over the pre-upgrade balance
// (`baseline`) — NOT a specific flag or absolute value — so it stays correct
// regardless of the exact grant size and independent of which /api/credits
// fields ship. Bounded, so a lagging webhook resolves to a clear "finalizing"
// message instead of an infinite spinner. Returns true once the grant is
// visible, false on timeout.
async function waitForProGrant(
  baseline: number,
  attempts = 8,
  intervalMs = 1500,
): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const cur = await fetchRemainingCredits();
    if (cur != null && cur > baseline) return true;
  }
  return false;
}

export function TrialExhaustedModal({
  open,
  onClose,
  onConverted,
  headline,
  subheading,
  pill,
  dismissLabel,
  creditsRemaining = null,
  requiredCredits = null,
  reason = null,
  returnTo = null,
}: {
  open: boolean;
  onClose: () => void;
  /** Called once the $29 has settled, so the parent can sync entitlement.
   *  Fires on EVERY charged exit — the credit grant landing, the "Continue"
   *  button on the finalizing notice, and a ✕/backdrop/Esc dismissal from
   *  either of those states. A plain "Not now" never reaches it. */
  onConverted?: () => void;
  headline?: string | null;
  subheading?: string | null;
  // Optional copy overrides so this same modal can front a proactive "Convert
  // to Pro now" CTA (user still has credits) without falsely reading "Out of
  // trial credits". Default to the exhaustion copy so existing callers are
  // unchanged.
  pill?: string | null;
  dismissLabel?: string | null;
  /** Trial-pool balance when the gate fired. Positive → the user still has
   *  credits and just can't afford THIS action, so the copy must NOT claim the
   *  trial credits are used up (the reported bug). 0 → genuinely empty pool. */
  creditsRemaining?: number | null;
  /** Credits the blocked action needed to start (the server `min_cost`). */
  requiredCredits?: number | null;
  /** Server gate reason ("pool_empty" | "insufficient_balance") — analytics. */
  reason?: string | null;
  /** Where to land the buyer once the conversion settles. Null means the caller
   *  did not say, and the page they came from is used instead. */
  returnTo?: string | null;
}) {
  const [convert, setConvert] = useState<ConvertCopy | null>(null);
  const [state, setState] = useState<
    "idle" | "working" | "converting" | "finalizing" | "carding"
  >("idle");
  // A non-null error means the last upgrade attempt failed. It's rendered above
  // the CTA and cleared on the next attempt — the button returns to idle so the
  // user can retry (acceptance criteria: no silent failures, always retryable).
  const [error, setError] = useState<string | null>(null);
  // Set when the server says the failure is one the USER can fix — an issuer
  // decline (needs a different card) or an SCA challenge (needs the card
  // confirmed on-session). Both are unfixable by pressing the same button
  // again, which is all the modal used to offer: the copy said "update your
  // card" while the only control replayed the identical off-session charge.
  const [canUpdateCard, setCanUpdateCard] = useState(false);

  // Reset the attempt state whenever the modal (re)opens — React's "adjust state
  // on prop change during render" pattern (not a setState-in-effect). The modal
  // is mounted unconditionally, so without this a reopen inherited a stale
  // spinner (dismissed mid-flight) or a stale error (a POST that failed after
  // close). Every open is now a clean slate.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setState("idle");
      setError(null);
      setCanUpdateCard(false);
    }
  }

  // `onConverted` now also SHOWS the post-upgrade confirmation, so one
  // conversion must reach the parent exactly once — but a dismissal while the
  // grant poll is still running fires it twice: once for the charged dismissal,
  // and again when the abandoned poll sees the credits land. Latched per
  // ATTEMPT (reset when a new convert POST starts), not per open: a genuinely
  // new purchase deserves a new confirmation, a raced exit does not.
  const convertedNotifiedRef = useRef(false);
  const notifyConverted = () => {
    if (convertedNotifiedRef.current) return;
    convertedNotifiedRef.current = true;
    onConverted?.();
  };

  // Once the convert POST has answered ok the $29 HAS settled — the account is
  // converting whatever the user does next. `converting` (waiting on the credit
  // grant) and `finalizing` (the grant outran our poll window) are both that
  // state, and both are dismissable: the ✕, the backdrop and Esc are all live.
  //
  // Dismissing from either used to hand the parent nothing but `onClose`, so a
  // charged conversion that the user closed early never told the app to re-read
  // its entitlement — the surfaces around it stayed on trial state until a hard
  // refresh. Every charged exit now routes through `onConverted`, which is the
  // one hook parents use to start the post-purchase sync, so no parent has to
  // guess whether a dismissal was "not now" or "already paid".
  const handleClose = () => {
    const charged = state === "converting" || state === "finalizing";
    setError(null);
    setCanUpdateCard(false);
    if (charged) notifyConverted();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    // Carry the credit state onto the "shown" event so the funnel records the
    // balance, the action's estimate, the gate reason, and which copy variant
    // the user actually saw — the reported bug was invisible without it.
    trackTrialExhaustedShown({
      credits_remaining: creditsRemaining,
      required_credits: requiredCredits,
      gating_reason: reason,
      variant:
        typeof creditsRemaining === "number" && creditsRemaining > 0
          ? "insufficient_balance"
          : "zero_balance",
    });
    fetch("/api/billing/trial/status")
      .then((r) => r.json())
      .then((d) => {
        if (d?.active) setConvert(d.convert as ConvertCopy);
      })
      .catch(() => {});
  }, [open, creditsRemaining, requiredCredits, reason]);

  async function handleConvert() {
    // CTA clicked → request started → succeeded | failed(reason): the full
    // early-convert funnel, so a stuck CTA is observable end-to-end.
    trackTrialConvertClicked();
    // Converting charges the saved card for the first Pro month, so it hits the
    // same wall. Guarded after the click event and BEFORE trackTrialConvertStarted
    // — nothing was started, and counting a start here would leave a permanently
    // unresolved leg in the convert funnel.
    if (
      interceptIfPaymentsPaused({
        kind: "upgrade",
        surface: "trial_exhausted_modal",
        label: "Pro",
        priceLabel: convert?.priceLabel ?? null,
        plan: "pro",
        cadence: "monthly",
      })
    ) {
      return;
    }
    trackTrialConvertStarted();
    setError(null);
    setCanUpdateCard(false);
    // A fresh attempt may notify again — the latch only dedupes exits of THIS
    // conversion (dismissal vs. the poll it abandoned), never a retry.
    convertedNotifiedRef.current = false;
    setState("working");

    // WHOP CONVERTS AT A CHECKOUT, SO SEND THE BUYER TO OURS.
    //
    // Whop cannot end a trial early in place — the arm in
    // app/api/billing/trial/convert spells out why — so a conversion there is
    // always a fresh checkout that bills today, and it always answers
    // `approval_required`. That used to mean one thing only: a redirect to a
    // Whop-branded page, the last purchase in the app that still left it.
    //
    // Navigating BEFORE the POST rather than after it is deliberate. /checkout
    // calls this same route itself, so deciding here on the response would mint
    // the conversion twice and abandon the first — and this route is not a
    // plain quote: it tags the replacement with the membership it supersedes.
    // One initiation, one mint.
    //
    // Gated on the provider, so Stripe, Paddle and Chargebee keep the settle-
    // in-place path below untouched (they charge the saved card and never reach
    // a checkout at all), and PayPal keeps its redirect.
    //
    // `returnTo` used to read "/settings/billing", flat. This modal fronts every
    // upgrade CTA in the product — the builder, the pricing page, the credit
    // chip, the feature gates — so that one literal took every converting user
    // to Settings whatever they had been doing, including a buyer who had just
    // clicked a plan on /pricing. The caller names the destination when it knows
    // one; otherwise the page they came from answers for them.
    const destination = returnTo ?? resolvePurchaseReturnPath();
    if (isWhop() && isCustomCheckoutEnabled()) {
      window.location.assign(
        customCheckoutHref({ type: "convert", returnTo: destination }),
      );
      return;
    }

    // Snapshot the (exhausted) balance BEFORE converting, so we can detect the
    // Pro grant as a real increase over it. Defaults to 0 if the read fails —
    // the 300-credit grant will still clear it.
    const baseline = (await fetchRemainingCredits()) ?? 0;
    try {
      const res = await fetch("/api/billing/trial/convert", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The providers that hand back an approval URL (PayPal, and Whop when
        // the custom checkout is off) build it server-side, so the destination
        // has to travel with the request — the branch above never reaches them.
        body: JSON.stringify({ returnTo: destination }),
      });
      if (!res.ok) {
        // Non-2xx → show the server's actionable message and return to idle so
        // the click retries. NEVER a silent reset (the reported bug: button
        // loads, stops, nothing happens). The route sends { code, message } and
        // optionally { recovery } for each failure (authentication_required /
        // card_declined / update_failed / not_configured / …).
        const data = (await res.json().catch(() => null)) as {
          code?: string;
          message?: string;
          recovery?: string;
        } | null;
        trackTrialConvertFailed(data?.code || `http_${res.status}`, res.status);
        setError(
          data?.message ||
            "We couldn't start your upgrade. Please try again in a moment.",
        );
        // The server distinguishes "this card can't be charged silently" from
        // "our side broke". Only the former gets the card button — offering it
        // for a config outage would send the user to Stripe for nothing.
        setCanUpdateCard(data?.recovery === "update_card");
        setState("idle");
        return;
      }
      // ── A provider that needs the payer to approve first ──────────────
      //
      // On PayPal the trial cannot be converted by an off-session charge (the
      // account holds no reference-transactions entitlement), so the route
      // mints a replacement subscription that bills today and hands back its
      // approval URL. There is no settled payment to report yet, and no grant
      // to poll for — the money moves when the payer approves at PayPal, and
      // the return reconciles it. Anything else here would strand them on a
      // spinner waiting for credits that cannot arrive in this tab.
      const body = (await res.json().catch(() => null)) as {
        url?: string;
      } | null;
      if (body?.url) {
        // Deliberately NOT trackTrialConvertSucceeded: nothing has been paid.
        // Counting a success on the hand-off would book every abandoned
        // approval as a conversion.
        window.location.assign(body.url);
        return;
      }

      // res.ok means the $29 actually SETTLED (error_if_incomplete), so the
      // conversion succeeded. The Pro credits land via the async webhook — wait
      // for them before handing back, so we never close on a stale 0 balance.
      trackTrialConvertSucceeded();
      setState("converting");
      const landed = await waitForProGrant(baseline);
      if (landed) {
        notifyConverted();
        return;
      }
      // Charged and converted, but the credit-grant webhook is lagging past our
      // poll window. Don't strand the user on a spinner and don't silently close
      // at 0 — tell them plainly it's done + finalizing, and let them continue.
      setState("finalizing");
    } catch {
      // Network/abort — the request never reached a response.
      trackTrialConvertFailed("network_error");
      setError(
        "We couldn't reach the server. Check your connection and try again.",
      );
      setState("idle");
    }
  }

  // The recovery the modal never had. Hosted Stripe Checkout in SETUP mode is
  // the surface that can do the two things an off-session charge cannot: run a
  // 3DS challenge with the cardholder present, and accept a different card.
  // intent:"update" is what makes the webhook promote the result to the default
  // payment method on the customer AND on every active subscription — including
  // this trial sub (app/api/stripe/webhook/route.ts:436-460) — so coming back
  // and pressing Upgrade charges the new, now-authenticated card.
  async function handleUpdateCard() {
    trackTrialConvertCardUpdate();
    // A card update is a Stripe SetupIntent session — same outage, same wall.
    // No label/price: nothing is being bought, so the pop-up drops that row.
    if (
      interceptIfPaymentsPaused({
        kind: "upgrade",
        surface: "trial_exhausted_update_card",
        plan: "pro",
      })
    ) {
      return;
    }
    setError(null);
    setState("carding");
    try {
      const res = await fetch("/api/stripe/setup-card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intent: "update",
          // Return to wherever the modal was opened from, so the user lands back
          // on the page they were blocked on rather than a generic dashboard.
          returnTo:
            typeof window !== "undefined"
              ? window.location.pathname + window.location.search
              : undefined,
        }),
      });
      const data = (await res.json().catch(() => null)) as {
        url?: string;
        error?: string;
      } | null;
      if (!res.ok || !data?.url) {
        setError(
          data?.error ||
            "We couldn't open the card form. Please try again in a moment.",
        );
        setState("idle");
        return;
      }
      redirectToBilling(data.url);
    } catch {
      setError(
        "We couldn't reach the server. Check your connection and try again.",
      );
      setState("idle");
    }
  }

  const price = convert?.priceLabel ?? "$29";
  const credits = convert?.credits ?? 300;

  // Badge + subheading depend on whether the trial pool is genuinely empty or
  // merely too low for this action. Resolved centrally (and unit-tested) so the
  // modal never contradicts the balance the user can see.
  const { badge, subheading: resolvedSubheading } = resolveTrialExhaustedCopy({
    creditsRemaining,
    requiredCredits,
    proCredits: credits,
  });

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[100] bg-black/50 supports-backdrop-filter:backdrop-blur-sm",
            "data-open:animate-in data-open:fade-in-0",
            "data-closed:animate-out data-closed:fade-out-0",
          )}
        />
        <DialogPrimitive.Popup
          className={cn(
            // The popup is the FRAME, never the scroller — the card inside owns
            // the one scroll region, which keeps the ✕ pinned while the body
            // scrolls. Capped to the DYNAMIC viewport so a mobile browser's
            // visible toolbars can't push the card off screen. (PANDA-1565)
            "fixed inset-0 z-[100] flex flex-col overflow-hidden sm:rounded-3xl",
            "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-[calc(100%-2rem)] sm:max-w-140 sm:-translate-x-1/2 sm:-translate-y-1/2",
            MODAL_VIEWPORT_FRAME_SM,
            "data-open:animate-in data-open:fade-in-0 sm:data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 sm:data-closed:zoom-out-95",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Upgrade to Pro to keep building
          </DialogPrimitive.Title>

          <div
            className={cn(
              "relative flex w-full max-h-full flex-col overflow-hidden bg-card text-card-foreground",
              // Mirrors the reference Shell (rated-credits-flow) exactly: full-bleed
              // sheet on phones, re-capped to a centered 35rem card from sm up.
              "h-full max-w-none rounded-none shadow-none ring-0 sm:h-auto sm:max-w-140 sm:rounded-3xl sm:shadow-2xl sm:ring-1 sm:ring-foreground/10",
            )}
          >
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleClose}
              aria-label="Close"
              className="absolute right-4 top-4 z-20 rounded-full bg-card/80 text-foreground/60 backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-5" />
            </Button>
            {/* The one scroll region. Padding lives here (not on the card) so the
                pinned ✕ above stays put while this scrolls, and the scrollbar
                runs the card's full height. Tightened on short viewports so the
                common laptop case needs no scroll at all. (PANDA-1565) */}
            <div
              className={cn(
                MODAL_SCROLL_BODY,
                "p-6 short:p-5 sm:p-8 sm:short:p-6",
              )}
            >

              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1.5 text-xs font-extrabold text-emerald-400">
                <span className="size-1.5 rounded-full bg-emerald-400" />
                {/* An explicit `pill` override (proactive "Convert to Pro now" CTA)
                    wins; otherwise fall back to the resolved badge that distinguishes
                    an empty pool from merely-too-low-for-this-action. */}
                {pill || badge}
              </div>

              <h2 className="pr-8 text-3xl font-extrabold leading-tight tracking-tight">
                {headline || `You're building fast — unlock ${credits} more`}
              </h2>
              <p className="mt-2.5 text-[15px] font-medium text-muted-foreground">
                {subheading || resolvedSubheading}
              </p>

              <div className="mt-5 rounded-2xl p-5 ring-2 ring-emerald-600">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-extrabold tracking-tight">
                    Pro plan
                  </h3>
                  <span className="rounded-full bg-panda-gradient-success px-2.5 py-1 text-[11px] font-extrabold text-white">
                    Most popular
                  </span>
                </div>

                <p className="mt-3.5 text-[13px] font-extrabold">
                  Upgrade now and get:
                </p>
                <ul className="mt-3 grid gap-2.5">
                  {[
                    `${credits} credits, live the moment you upgrade`,
                    "Custom domain, no Panda branding",
                    "Unused credits roll over, never wasted",
                  ].map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-[13.5px] font-medium"
                    >
                      <Check className="mt-0.5 size-4 flex-none text-emerald-400" />
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex items-baseline justify-between border-t border-border pt-4">
                  <span className="text-[13px] font-semibold text-muted-foreground">
                    Billed monthly
                  </span>
                  <span className="text-[27px] font-extrabold leading-none tracking-tight">
                    {price}
                    <span className="text-sm font-semibold text-muted-foreground">
                      {" "}
                      /mo
                    </span>
                  </span>
                </div>
                <p className="mt-1.5 text-right text-xs text-muted-foreground">
                  starts today · your trial ends now · cancel anytime
                </p>
              </div>

              {error ? (
                <p
                  role="alert"
                  className="mt-6 rounded-xl bg-red-500/10 px-3.5 py-2.5 text-[13.5px] font-medium text-red-500"
                >
                  {error}
                </p>
              ) : state === "finalizing" ? (
                <p className="mt-6 rounded-xl bg-emerald-500/10 px-3.5 py-2.5 text-[13.5px] font-medium text-emerald-500">
                  You&apos;re on Pro — your {credits} credits are finalizing and
                  will appear in a moment.
                </p>
              ) : null}

              {/* When the card is what blocked the charge, fixing the card IS the
                  primary action — a plain "Try again" would replay the identical
                  off-session charge and fail identically. Retry stays available
                  below it for the case where the user fixed things elsewhere. */}
              {canUpdateCard ? (
                <>
                  <Button
                    size="lg"
                    onClick={handleUpdateCard}
                    // Also disabled mid-retry, so a click can't navigate to Stripe
                    // while a charge is in flight and orphan its result.
                    disabled={state === "carding" || state === "working"}
                    variant="success"
                    className="mt-3 h-12 w-full rounded-xl text-[15px]"
                  >
                    {state === "carding" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Opening secure card form…
                      </>
                    ) : (
                      "Confirm or change your card"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleConvert}
                    disabled={state === "working" || state === "carding"}
                    className="mt-2 h-11 w-full rounded-xl text-sm font-semibold"
                  >
                    {state === "working" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Retrying…
                      </>
                    ) : (
                      "Try the same card again"
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  size="lg"
                  onClick={
                    state === "finalizing" ? notifyConverted : handleConvert
                  }
                  disabled={state === "working" || state === "converting"}
                  variant="success"
                  className={cn(
                    "h-12 w-full rounded-xl text-[15px]",
                    error || state === "finalizing" ? "mt-3" : "mt-6",
                  )}
                >
                  {state === "working" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Upgrading…
                    </>
                  ) : state === "converting" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Unlocking your credits…
                    </>
                  ) : state === "finalizing" ? (
                    "Continue"
                  ) : error ? (
                    "Try again"
                  ) : (
                    "Upgrade and keep building"
                  )}
                </Button>
              )}

              <Button
                variant="ghost"
                onClick={handleClose}
                className="mt-2 h-11 w-full rounded-xl text-sm font-semibold text-muted-foreground hover:text-foreground"
              >
                {dismissLabel || "Not now — I'll come back before my trial ends"}
              </Button>

              <p className="mt-5 text-[13px] font-medium leading-relaxed text-muted-foreground/80">
                Your free trial covered your first credits. Upgrading starts your Pro
                plan today at {price}/mo.
              </p>
            </div>
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
